/**
 * Papers that belong to the car: a Carfax report, a window sticker, an
 * auction condition report.
 *
 * Kept apart from the document archive on purpose. That one holds what was
 * printed and signed and is retained ten years; nothing in the app can delete
 * from it. These get superseded — you pull a fresher Carfax and the old one
 * stops being the truth — so they can be replaced and removed.
 */

import { supabase } from "../../lib/supabase";
import { shortenUrl } from "../../lib/stripe";

export const BUCKET = "vehicle-docs";

/** Bigger than any history report, small enough that a mis-picked file is
    caught before it is uploaded over a phone connection. */
const MAX_BYTES = 25 * 1024 * 1024;

export type VehicleDocKind =
  | "carfax" | "window_sticker" | "inspection" | "auction_report" | "other";

export const DOC_KINDS: { value: VehicleDocKind; label: string }[] = [
  { value: "carfax",         label: "Carfax" },
  { value: "window_sticker", label: "Window sticker" },
  { value: "inspection",     label: "Inspection" },
  { value: "auction_report", label: "Auction report" },
  { value: "other",          label: "Other" },
];

export const KIND_LABEL: Record<string, string> =
  Object.fromEntries(DOC_KINDS.map((k) => [k.value, k.label]));

export interface VehicleDocument {
  id: string;
  vehicle_id: string;
  kind: VehicleDocKind;
  filename: string;
  storage_path: string;
  byte_size: number | null;
  reported_on: string | null;
  notes: string | null;
  /** Short public code. Null means the document is not shared. */
  share_code: string | null;
  /** Cached shortened link, so it is not fetched on every listing. */
  short_url: string | null;
  created_at: string;
}

export class VehicleDocError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "VehicleDocError";
  }
}

export async function listVehicleDocs(vehicleId: string): Promise<VehicleDocument[]> {
  const { data, error } = await supabase
    .from("vehicle_documents")
    .select("*")
    .eq("vehicle_id", vehicleId)
    .order("created_at", { ascending: false });
  if (error) throw new VehicleDocError(`Could not read the documents: ${error.message}`);
  return (data ?? []) as VehicleDocument[];
}

export async function uploadVehicleDoc(
  vehicleId: string,
  file: File,
  kind: VehicleDocKind,
  reportedOn: string | null,
): Promise<VehicleDocument> {
  // Checked by content type and by extension: Safari has been known to send
  // an empty type for a file picked out of Files.
  const looksPdf = file.type === "application/pdf"
    || file.name.toLowerCase().endsWith(".pdf");
  if (!looksPdf) throw new VehicleDocError("That is not a PDF.");
  if (file.size > MAX_BYTES) {
    throw new VehicleDocError(
      `That file is ${(file.size / 1048576).toFixed(1)} MB. The limit is 25 MB.`);
  }

  const safe = file.name.replace(/[^A-Za-z0-9._-]/g, "-").slice(-80);
  const path = `${vehicleId}/${Date.now()}-${safe}`;

  const up = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: "application/pdf", upsert: false });
  if (up.error) {
    const msg = up.error.message;
    if (/bucket/i.test(msg) && /not found|does not exist/i.test(msg)) {
      throw new VehicleDocError(
        "The vehicle-docs bucket does not exist yet. Run 16_vehicle_documents.sql "
        + "in Supabase.", up.error);
    }
    throw new VehicleDocError(`Could not upload: ${msg}`, up.error);
  }

  const { data, error } = await supabase
    .from("vehicle_documents")
    .insert({
      vehicle_id: vehicleId, kind, filename: file.name,
      storage_path: path, byte_size: file.size, reported_on: reportedOn,
    })
    .select("*")
    .single();

  if (error || !data) {
    // Nothing points at it, so it would sit in storage unreachable forever.
    await supabase.storage.from(BUCKET).remove([path]);
    throw new VehicleDocError(
      `The file uploaded but could not be filed: ${error?.message ?? "no row returned"}`,
      error);
  }
  return data as VehicleDocument;
}

/** File first, then the row: the other order leaves an orphan behind. */
export async function deleteVehicleDoc(doc: VehicleDocument) {
  const { error: storageError } = await supabase.storage
    .from(BUCKET).remove([doc.storage_path]);
  if (storageError) {
    throw new VehicleDocError(`Could not remove the file: ${storageError.message}`);
  }
  const { error } = await supabase
    .from("vehicle_documents").delete().eq("id", doc.id);
  if (error) throw new VehicleDocError(`Could not remove it: ${error.message}`);
}

/** Fetch the bytes and open them. Private bucket, so this goes through the
    signed-in session rather than a URL that could be forwarded. */
export async function openVehicleDoc(doc: VehicleDocument) {
  const { data, error } = await supabase.storage.from(BUCKET).download(doc.storage_path);
  if (error || !data) {
    throw new VehicleDocError(
      `Could not open it: ${error?.message ?? "not found"}`);
  }
  const url = URL.createObjectURL(data);
  window.open(url, "_blank", "noopener");
  // Revoking at once can cancel the open in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * A link a customer can open without signing in.
 *
 * The bucket is private, so this asks Supabase for a signed URL: a temporary
 * address that works for anyone holding it and then stops. Seven days is long
 * enough to email a buyer and short enough that a forwarded link does not
 * stay live indefinitely.
 *
 * Worth knowing before sending one: anybody with the link can open the
 * report until it expires. A Carfax names previous owners' states, mileage
 * readings and accident history — it is the car's record, not the customer's,
 * but it is not nothing.
 */
const WORKER = import.meta.env.VITE_PAYMENTS_WORKER as string | undefined;

/**
 * A short link, safe to paste into an advert.
 *
 * The code is stored on the document; the worker exchanges it for a fresh
 * signed URL on each visit. That keeps the public link under sixty characters
 * and stops it expiring while the listing is still running — a Carfax link
 * that dies after a week is worse than no link on an ad that runs a month.
 */
export async function shortShareLink(doc: VehicleDocument): Promise<string> {
  if (!WORKER) {
    throw new VehicleDocError(
      "Short links need VITE_PAYMENTS_WORKER set. Using the long link instead.");
  }
  let code = doc.share_code;
  if (!code) {
    const { data, error } = await supabase
      .rpc("new_share_code")
      .then(async (r) => {
        if (r.error) return { data: null, error: r.error };
        return supabase.from("vehicle_documents")
          .update({ share_code: r.data as string })
          .eq("id", doc.id).select("share_code").single();
      });
    if (error || !data?.share_code) {
      throw new VehicleDocError(
        `Could not make a short link: ${error?.message ?? "no code returned"}`);
    }
    code = data.share_code;
  }
  const full = `${WORKER.replace(/\/$/, "")}/c/${code}`;

  /* Shortened once and kept. The shortener hides the account subdomain, and
     doing it by hand for every listing is the step that gets skipped. If it
     cannot be reached the full link is returned, which works just as well. */
  if (doc.short_url) return doc.short_url;
  try {
    const { short } = await shortenUrl(full);
    if (!short) return full;
    await supabase.from("vehicle_documents")
      .update({ short_url: short }).eq("id", doc.id);
    return short;
  } catch {
    return full;
  }
}

/** Stop every link that was handed out for this document. */
export async function revokeShareLink(doc: VehicleDocument) {
  const { error } = await supabase.from("vehicle_documents")
    .update({ share_code: null }).eq("id", doc.id);
  if (error) throw new VehicleDocError(`Could not revoke it: ${error.message}`);
}

export async function shareLink(doc: VehicleDocument, days = 7): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(doc.storage_path, days * 24 * 60 * 60, {
      // Opens in the browser rather than downloading, which is what somebody
      // tapping a link on a phone expects.
      download: false,
    });
  if (error || !data?.signedUrl) {
    throw new VehicleDocError(
      `Could not make a link: ${error?.message ?? "no URL returned"}`);
  }
  return data.signedUrl;
}

export function formatSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) return "";
  const mb = bytes / 1048576;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}
